import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { UserProvider, useUser } from './context/UserContext';
import { ThemeProvider } from './context/ThemeContext';
import { SearchProvider } from './context/SearchContext';
import { NotificationProvider } from './context/NotificationContext';
import UnauthenticatedRoutes from './routing/UnauthenticatedRoutes';
import AuthenticatedRoutes from './routing/AuthenticatedRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import tokenManager from './utils/tokenManager';
import DomainUtils from './utils/domainUtils';
import './index.css';
import logger from './utils/logger';

// Silence non-error console output in production.
// New code should use src/utils/logger.js instead of console directly.
if (process.env.NODE_ENV === 'production') {
  logger.log = () => {};
  logger.warn = () => {};
  logger.debug = () => {};
}

// Inner App component that uses UserContext
const AppContent = () => {
  const { 
    isAuthenticated, 
    loading, 
    isInitialized,
    userEmail,
    firstName,
    lastName,
    userData,
    hasAdminAccess,
    refreshAuth,
    logout
  } = useUser();

  // No auto-redirect - show access denied page instead

  // Wait for auth to initialize before rendering routes
  if (!isInitialized || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show access denied if authenticated but no admin access
  if (isAuthenticated && !hasAdminAccess) {
    const isBusinessUser = userData?.accountType === 'business' ||
                          userData?.userType === 'business' ||
                          userData?.isBusinessOwner;

    const hasNoActivePlan = isBusinessUser &&
                           userData?.business?.subscriptionDetails?.plan === null;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-lg w-full p-10 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
            {hasNoActivePlan ? (
              <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            ) : (
              <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
            )}
          </div>

          {/* Title & Message */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              {hasNoActivePlan ? 'Complete Your Business Setup' : 'Access Restricted'}
            </h1>
            <p className="text-gray-600 text-base leading-relaxed">
              {hasNoActivePlan
                ? 'Select a subscription plan to access your admin dashboard and unlock all features.'
                : 'You don\'t have the required permissions to access this dashboard.'
              }
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {hasNoActivePlan ? (
              <button
                onClick={() => window.location.href = DomainUtils.buildUrl('accounts', '/business-setup')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-base shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Complete Business Setup
              </button>
            ) : (
              <button
                onClick={() => window.location.href = DomainUtils.buildUrl('accounts', '/profile')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-base shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account Settings
              </button>
            )}

            <button
              onClick={() => window.location.href = DomainUtils.buildUrl('mail', '/inbox')}
              className="w-full bg-white border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-base"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Go to Inbox
            </button>

            <button
              onClick={logout}
              className="w-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-base"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>

          {/* User Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Signed in as <span className="font-semibold text-gray-900">{userEmail}</span>
            </p>
          </div>

          {/* Additional Info for Business Setup */}
          {hasNoActivePlan && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-left">
                  <p className="text-sm font-medium text-blue-900 mb-1">Quick Setup</p>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Get started with a 6-month free trial. No credit card required. Choose from our flexible plans to match your business needs.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <SearchProvider>
          <div className="App admin-app">
            {isAuthenticated ? (
              <ErrorBoundary>
                <AuthenticatedRoutes 
                  userEmail={userEmail}
                  userFirstName={firstName}
                  userLastName={lastName}
                  hasAdminAccess={hasAdminAccess}
                />
              </ErrorBoundary>
            ) : (
              <ErrorBoundary>
                <UnauthenticatedRoutes 
                  onLoginSuccess={refreshAuth}
                />
              </ErrorBoundary>
            )}
          </div>
        </SearchProvider>
      </Router>
    </ErrorBoundary>
  );
};

// Main App component
const App = () => {
  useEffect(() => {
    // Initialize token manager and clean up legacy storage
    tokenManager.cleanupLegacyStorage();
    
    // Add admin app styling
    document.body.className = 'admin-app';
    
    
    return () => {
      document.body.className = '';
    };
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="admin-ui-theme">
        <NotificationProvider>
          <UserProvider>
            <AppContent />
          </UserProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;